import { exportNodeSpecsAsJson, readFileAsText } from "../utils/thermalConfigUtils";
import { showToastMessage } from "../../../../../../../utils/commonFunctions";
import {
  EllipseIconWhite,
  OneNodeIcon,
  NineNodeIcon,
  TickIconWhite,
} from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useHandleModalOpen, useNodeSpecsModal } from "../hooks/ThermalDesignerHooks";
import { THERMAL_FACE_BODY_VEC, THERMAL_FACE_LABEL, THERMAL_FACE_ORDER } from "../../../ThermalDesignContext";
import type { ThermalFaceId } from "../../../ThermalDesignTypes";

import { DividerLine } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { useThermalDesign } from "../../../ThermalDesignContext";
import { ThermalMaterialDropdownInputItem, ThermalModalHeader, ThermalModalFooter, ThermalSingleInputItemWrapper } from "../components/common/ThermalDesignerCommonComponents";
import ConfirmationDialog from "../../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import { createPortal } from "react-dom";
import ThermalCoatingSection from "../components/ThermalCoatingSection";
import SubSystemsSection from "../components/SubSystemsSection";

// Module-level constant — derived from a static array, no need to recompute per render
const EXTERNAL_FACE_ROWS: ThermalFaceId[][] = [];
for (let i = 0; i < THERMAL_FACE_ORDER.length; i += 2) {
  EXTERNAL_FACE_ROWS.push([THERMAL_FACE_ORDER[i], THERMAL_FACE_ORDER[i + 1]]);
}

// Helper to avoid repeating the SSR guard on every portal call
const bodyPortal = (children: React.ReactNode) =>
  typeof document !== "undefined" ? createPortal(children, document.body) : null;

const NodeSpecsModal = () => {
  const { mission_id, sat_id } = useParams();
  const { nodeSpecsOptions, nodeSpecsUnits, isSatDesignerSidebarOpen, setOpenModal, materialsData, setNodeSpecsOptions } = useThermalDesign();
  const { handleModalOpen } = useHandleModalOpen();

  const {
    isModalMinimized,
    setIsModalMinimized,
    isDeleteConfirmationOpen,
    nodeToDelete,
    invalidFields,
    setInvalidFields,
    selectedNode,
    convertToNumber,
    isLastAvailableNode,
    handleSaveAndExit,
    handleSaveAndNext,
    handleSelectNode,
    handleConfirmDelete,
    handleCancelDelete,
    handleMaterialSelection,
    handleCancelConfirm,
  } = useNodeSpecsModal();

  const materialOptions = useMemo(
    () =>
      materialsData.map((material) => ({
        label: material.material_name,
        value: material.material_name,
      })),
    [materialsData],
  );

  const selectedMaterial = selectedNode?.values?.node_material || null;

  // nodes_per_face is global — all nodes (external faces + internal decks) share the same value
  const selectedDistribution: 1 | 5 | 9 = (nodeSpecsOptions[0]?.nodes_per_face ?? 1) as 1 | 5 | 9;

  const [faceType, setFaceType] = useState<"external" | "internal">(selectedNode?.values?.node_isInternal ? "internal" : "external");
  const [lastExternalNodeId, setLastExternalNodeId] = useState<string | null>(null);
  const [lastInternalNodeId, setLastInternalNodeId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedNode) return;
    setFaceType(selectedNode.values?.node_isInternal ? "internal" : "external");
  }, [selectedNode?.id]);

  useEffect(() => {
    if (!selectedNode) return;
    const isInternal = selectedNode.values?.node_isInternal ?? false;
    if (isInternal) setLastInternalNodeId(selectedNode.id);
    else setLastExternalNodeId(selectedNode.id);
  }, [selectedNode]);

  const externalFaceRows = EXTERNAL_FACE_ROWS;

  const selectedExternalFaceId: ThermalFaceId | null = useMemo(() => {
    if (!selectedNode || selectedNode.values?.node_isInternal) return null;
    const vecKey = (selectedNode.values?.node_body_vec ?? []).join(",");
    return THERMAL_FACE_ORDER.find((faceId) => THERMAL_FACE_BODY_VEC[faceId].join(",") === vecKey) ?? null;
  }, [selectedNode]);

  const handleSelectExternalFace = (faceId: ThermalFaceId) => {
    const faceVecKey = THERMAL_FACE_BODY_VEC[faceId].join(",");
    const faceNode = nodeSpecsOptions.find((n) => !(n.values?.node_isInternal ?? false) && (n.values?.node_body_vec ?? []).join(",") === faceVecKey);
    if (!faceNode) return;

    setFaceType("external");
    handleSelectNode(faceNode.id);
  };

  const selectedFaceTitle = useMemo(() => {
    if (selectedExternalFaceId) return THERMAL_FACE_LABEL[selectedExternalFaceId];
    return selectedNode?.name ?? "";
  }, [selectedExternalFaceId, selectedNode?.name]);

  // const internalDistances = useMemo(() => {
  //   const distances = selectedNode?.values?.node_internal_distances ?? [];
  //   const byId = new Map<number, number>(distances.map((d) => [d.id, d.distance]));
  //   return {
  //     d1: byId.get(1) ?? 0,
  //     d2: byId.get(2) ?? 0,
  //   };
  // }, [selectedNode?.values?.node_internal_distances]);

  // const setInternalDistance = (id: 1 | 2, distance: number) => {
  //   if (!selectedNode) return;

  //   setNodeSpecsOptions((prev) =>
  //     prev.map((node) => {
  //       if (node.id !== selectedNode.id) return node;

  //       const baseValues = node.values ?? selectedNode.values!;
  //       const existing = baseValues.node_internal_distances ?? [];
  //       const next = [
  //         { id: 1 as const, distance: existing.find((d) => d.id === 1)?.distance ?? 0 },
  //         { id: 2 as const, distance: existing.find((d) => d.id === 2)?.distance ?? 0 },
  //       ].map((d) => (d.id === id ? { ...d, distance } : d));

  //       return {
  //         ...node,
  //         values: {
  //           ...baseValues,
  //           node_internal_distances: next,
  //         },
  //       };
  //     }),
  //   );
  // };

  const NodeCountSelector = () => (
    <>
      <div className="infocard-units-location-value-div">
        <div className="infocard-nodes-name-delete-div">
          <span>{"NODES PER FACE"}</span>
        </div>
      </div>

      <div className="infocard-units-location-value-div">
        <div className="node-distribution-selector-div">
          <button
            className={`node-distribution-selector-item ${selectedDistribution === 1 ? "active" : ""}`}
            onClick={() => handleDistributionSelect(1)}
            type="button"
          >
            <OneNodeIcon isActive={selectedDistribution === 1} />
            <span>1 Node</span>
          </button>
          <button
            className={`node-distribution-selector-item ${selectedDistribution === 9 ? "active" : ""}`}
            onClick={() => handleDistributionSelect(9)}
            type="button"
          >
            <NineNodeIcon isActive={selectedDistribution === 9} />
            <span>9 Nodes</span>
          </button>
        </div>
      </div>
    </>
  )

  const selectFirstNodeByType = (type: "external" | "internal") => {
    const shouldBeInternal = type === "internal";
    const match = nodeSpecsOptions.find((n) => (n.values?.node_isInternal ?? false) === shouldBeInternal);
    if (match) handleSelectNode(match.id);
  };

  const handleFaceTypeSelect = (type: "external" | "internal") => {
    setFaceType(type);

    const shouldBeInternal = type === "internal";
    const selectedIsInternal = selectedNode?.values?.node_isInternal ?? false;
    if (shouldBeInternal === selectedIsInternal) return;

    const preferredId = shouldBeInternal ? lastInternalNodeId : lastExternalNodeId;
    const preferred = preferredId ? nodeSpecsOptions.find((n) => n.id === preferredId) : undefined;
    if (preferred) {
      handleSelectNode(preferred.id);
      return;
    }

    selectFirstNodeByType(type);
  };

  const handleDistributionSelect = (nodesPerFace: 1 | 5 | 9) => {
    // Decks share nodes_per_face with external nodes — apply to all
    setNodeSpecsOptions((prev) => prev.map((node) => ({ ...node, nodes_per_face: nodesPerFace })));
  };

  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);

  const handleSaveClick = () => {
    setIsSaveConfirmationOpen(true);
  };

  const handleConfirmSave = () => {
    setIsSaveConfirmationOpen(false);
    if (mission_id && sat_id) {
      handleSaveAndExit(mission_id, sat_id);
    }
  };

  const handleExportJson = () => {
    exportNodeSpecsAsJson(nodeSpecsOptions);
  };

  const handleImport = async (file: File) => {
    try {
      const text = await readFileAsText(file);
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        setNodeSpecsOptions(parsed);
        showToastMessage("success", "Node specs imported successfully");
      } else {
        showToastMessage("error", "Import failed: expected an array of nodes");
      }
    } catch {
      showToastMessage("error", "Import failed: could not parse file");
    }
  };

  const header = { title: "Surface Configuration", isModalMinimized, setIsModalMinimized, setOpenModal, modalItem: "isNodeSpecsModalOpen", onExportJson: handleExportJson, onImport: handleImport };

  return (
    <div className={`nodeSpecsModal ${!isSatDesignerSidebarOpen ? "left" : "right"} surfaceConfigModal`}>
      <ThermalModalHeader header={header} />

      {!isModalMinimized && (
        <>
          <div className="nodeSpecsModal__inputs">
            {/* <NodeCountSelector /> */}

            <div className="infocard-units-location-value-div">
              <div className="external-internal-face-thermal-buttons-div">
                <button type="button" onClick={() => handleFaceTypeSelect("external")} className={faceType === "external" ? "active" : ""}>
                  External Face
                </button>
                <button type="button" onClick={() => handleFaceTypeSelect("internal")} className={faceType === "internal" ? "active" : ""}>
                  Internal Face
                </button>
              </div>
            </div>

            {faceType === "external" && (
              <div className="infocard-units-location-value-div">
                <div className="external-face-selector-div">
                  {externalFaceRows.map((row) => (
                    <div key={row.join("-")} className="external-face-selector-row">
                      {row.map((faceId) => {
                        const faceVecKey = THERMAL_FACE_BODY_VEC[faceId].join(",");
                        const faceNode = nodeSpecsOptions.find((n) => (n.values?.node_body_vec ?? []).join(",") === faceVecKey);
                        const isSelected = selectedExternalFaceId === faceId;
                        const isConfigured = faceNode?.status === "complete";
                        const shortLabel = THERMAL_FACE_LABEL[faceId].split(" ")[0];

                        return (
                          <button
                            key={faceId}
                            type="button"
                            className={`external-face-selector-item ${isSelected ? "isSelected" : ""}`}
                            onClick={() => handleSelectExternalFace(faceId)}
                            disabled={!faceNode}
                          >
                            <div className="external-face-selector-item-text-div">
                              <span>
                                <EllipseIconWhite isActive={isConfigured} />
                                {shortLabel}
                              </span>
                              <span>{faceId}</span>
                            </div>
                            {isConfigured && <TickIconWhite isActive />}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {faceType === "internal" && (
              <div className="infocard-units-location-value-div">
                <div className="external-face-selector-div">
                  <div className="external-face-selector-row">
                    {(["INT1", "INT2"] as const).map((deckId, idx) => {
                      const deckNode = nodeSpecsOptions.find((n) => n.id === deckId);
                      const isSelected = selectedNode?.id === deckId;
                      const isConfigured = deckNode?.status === "complete";
                      const label = idx === 0 ? "Deck 1" : "Deck 2";
                      return (
                        <button
                          key={deckId}
                          type="button"
                          className={`external-face-selector-item ${isSelected ? "isSelected" : ""}`}
                          onClick={() => deckNode && handleSelectNode(deckNode.id)}
                          disabled={!deckNode}
                        >
                          <div className="external-face-selector-item-text-div">
                            <span>
                              <EllipseIconWhite isActive={isConfigured} />
                              {label}
                            </span>
                            <span>{deckId}</span>
                          </div>
                          {isConfigured && <TickIconWhite isActive />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="nodeSpecsModal__divider__div">
              <DividerLine />
            </div>

            <div className="infocard-units-location-value-div">
              <div className="external-face-title-thermal">{selectedFaceTitle}</div>
            </div>

            <ThermalSingleInputItemWrapper
              label="Mass"
              unit={nodeSpecsUnits.node_mass}
              value={selectedNode?.values?.node_mass}
              fieldName="node_mass"
              required
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              convertToNumber={convertToNumber}
            />

            <ThermalSingleInputItemWrapper
              label="Surface Area"
              unit={nodeSpecsUnits.node_surface_area}
              value={selectedNode?.values?.node_surface_area}
              fieldName="node_surface_area"
              required
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              convertToNumber={convertToNumber}
            />

            <ThermalSingleInputItemWrapper
              label="Initial Temperature"
              unit={nodeSpecsUnits.node_initial_temperature}
              value={selectedNode?.values?.node_initial_temperature}
              fieldName="node_initial_temperature"
              required
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              convertToNumber={convertToNumber}
            />

            <ThermalSingleInputItemWrapper
              label="Internal Power"
              unit={nodeSpecsUnits.node_internal_power}
              value={selectedNode?.values?.node_internal_power}
              fieldName="node_internal_power"
              required
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              convertToNumber={convertToNumber}
            />
            <ThermalMaterialDropdownInputItem
              label="Material"
              required
              materialOptions={materialOptions}
              selectedMaterial={selectedMaterial}
              onSelectMaterial={(value) => handleMaterialSelection(value)}
              onEditCustomMaterial={() => handleModalOpen("isCustomMaterialModalOpen")}
              materialProperties={selectedNode?.values?.node_material_properties}
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
            />

            {/* Thermal Coating Section */}
            <ThermalCoatingSection />

            {/* Subsystems Section */}
            <SubSystemsSection />

          </div>

          <div className="nodeSpecsModal__divider__div">
            <DividerLine />
          </div>

          <ThermalModalFooter
            onSave={handleSaveClick}
            onSaveAndNext={handleSaveAndNext}
            isLastNode={isLastAvailableNode()}
            onCancel={() => setIsCancelConfirmationOpen(true)}
          />
        </>
      )}

      {bodyPortal(
        <ConfirmationDialog
          type="update"
          open={isSaveConfirmationOpen}
          title="Save & Exit"
          description="Are you sure you want to save this thermal configuration ?"
          onConfirm={handleConfirmSave}
          onCancel={() => setIsSaveConfirmationOpen(false)}
          confirm_button_text="Save & Exit"
          cancel_button_text="Cancel"
        />
      )}

      {bodyPortal(
        <ConfirmationDialog
          type="delete"
          open={isCancelConfirmationOpen}
          title="Discard Changes"
          description="Are you sure you want to cancel? Any unsaved changes will be lost."
          onConfirm={() => {
            setIsCancelConfirmationOpen(false);
            handleCancelConfirm();
          }}
          onCancel={() => setIsCancelConfirmationOpen(false)}
          confirm_button_text="Discard"
          cancel_button_text="Keep Editing"
        />
      )}

      {bodyPortal(
        <ConfirmationDialog
          type="delete"
          open={isDeleteConfirmationOpen}
          title="Delete Node"
          description={`Are you sure you want to delete ${nodeToDelete ? ` ${nodeSpecsOptions.find((n) => n.id === nodeToDelete)?.name}` : "this node"}? This action cannot be undone.`}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          confirm_button_text="Delete"
          cancel_button_text="Cancel"
        />
      )}
    </div>
  );
};

export default NodeSpecsModal;
